// v2.10.x — Org Chart visualizer modal.
//
// Full-screen overlay (NOT a shadcn Dialog — those are width-capped) that
// renders the team-member hierarchy derived from `reports_to_id`. Pure
// display: it never writes to the database. Hierarchy is built client-side
// from the same `/api/team-members` payload the table already uses, so the
// chart and the table stay in lockstep without an extra round-trip.
//
// Multi-root: every member with `reports_to_id == null` is a root. They are
// rendered side-by-side under a synthetic "Organization" anchor so the PDF
// export and the on-screen layout both fit a single viewport.
//
// PDF export is hard-coded to A3 landscape (420mm × 297mm) per the task
// brief. The card geometry is rendered into an off-screen canvas first, then
// scaled with `S = min(availW/w, availH/h)` so the entire chart fits one
// page regardless of head-count.

import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  X,
  ZoomIn,
  ZoomOut,
  Download,
  Search,
  Crosshair,
  EyeOff,
  Eye,
  Maximize2,
  Key,
} from "lucide-react";
import type { TeamMemberRecord } from "@shared/v2/types";

// Identity-bound names live outside the permission matrix in
// server/v2/authorization.ts. They are matched on the team-member NAME
// string itself — the seed creates rows literally named "SDF MD",
// "SDF PMO", and "SDF Board Secretary" (those constants are
// SDF_MD_TEAM_MEMBER_NAME / SDF_PMO_TEAM_MEMBER_NAME /
// SDF_BOARD_SECRETARY_TEAM_MEMBER_NAME on the server). The Org Chart
// marks these with a key icon so it's obvious at a glance which slot
// holds each ceremonial role. If the server constants ever change they
// must change here too — these are mirrored, not imported, because the
// client does not bundle server code.
const IDENTITY_BINDINGS: Record<string, string> = {
  "sdf md": "SDF MD",
  "sdf pmo": "SDF PMO",
  "sdf board secretary": "Board Secretary",
};

function identityRoleFor(name: string): string | null {
  return IDENTITY_BINDINGS[name.trim().toLowerCase()] ?? null;
}

// ---------- layout primitives ----------

const CARD_W = 220;
const CARD_H = 96;
const H_GAP = 28;
const V_GAP = 56;

interface ChartNode {
  id: string;
  member: TeamMemberRecord | null; // null for the synthetic root
  children: ChartNode[];
  // Computed by applyLayout
  x: number;
  y: number;
  subtreeW: number;
}

function buildTree(members: TeamMemberRecord[]): ChartNode {
  const byId = new Map<string, ChartNode>();
  for (const m of members) {
    byId.set(m.id, { id: m.id, member: m, children: [], x: 0, y: 0, subtreeW: 0 });
  }
  // Cycle guard against bad data (server normally blocks cyclic writes).
  const isCyclic = (startId: string): boolean => {
    const visited = new Set<string>();
    let cursor: string | null | undefined = byId.get(startId)?.member?.reports_to_id ?? null;
    let hops = 0;
    while (cursor && hops < 64) {
      if (cursor === startId) return true;
      if (visited.has(cursor)) return true; // unrelated loop — also unsafe
      visited.add(cursor);
      cursor = byId.get(cursor)?.member?.reports_to_id ?? null;
      hops++;
    }
    return false;
  };
  const roots: ChartNode[] = [];
  for (const m of members) {
    const node = byId.get(m.id)!;
    const parentId = m.reports_to_id;
    if (parentId && byId.has(parentId) && !isCyclic(m.id)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const seen = new Set<string>();
  const sortRecursive = (n: ChartNode): void => {
    if (seen.has(n.id)) return;
    seen.add(n.id);
    n.children.sort((a, b) => {
      const sa = a.member?.sort_order ?? 0;
      const sb = b.member?.sort_order ?? 0;
      if (sa !== sb) return sa - sb;
      return (a.member?.name ?? "").localeCompare(b.member?.name ?? "");
    });
    for (const c of n.children) sortRecursive(c);
  };
  roots.sort((a, b) => {
    const sa = a.member?.sort_order ?? 0;
    const sb = b.member?.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    return (a.member?.name ?? "").localeCompare(b.member?.name ?? "");
  });
  for (const r of roots) sortRecursive(r);
  // Synthetic root so multi-root data renders as one tree.
  return { id: "__root__", member: null, children: roots, x: 0, y: 0, subtreeW: 0 };
}

// Tidy-tree-ish layout: each subtree gets a width equal to the sum of its
// children's widths (or its own card width if a leaf), then children are
// centred under their parent. Good enough for a few hundred employees.
function applyLayout(root: ChartNode): { width: number; height: number } {
  let maxDepth = 0;
  const measure = (n: ChartNode, depth: number): number => {
    if (depth > maxDepth) maxDepth = depth;
    if (n.children.length === 0) {
      n.subtreeW = CARD_W;
      return CARD_W;
    }
    let total = 0;
    for (const c of n.children) {
      total += measure(c, depth + 1);
    }
    total += H_GAP * (n.children.length - 1);
    n.subtreeW = Math.max(CARD_W, total);
    return n.subtreeW;
  };
  measure(root, 0);

  const place = (n: ChartNode, leftX: number, depth: number): void => {
    n.x = leftX + n.subtreeW / 2 - CARD_W / 2;
    n.y = depth * (CARD_H + V_GAP);
    let cursor = leftX + (n.subtreeW - n.children.reduce((a, c) => a + c.subtreeW, 0) - H_GAP * Math.max(0, n.children.length - 1)) / 2;
    for (const c of n.children) {
      place(c, cursor, depth + 1);
      cursor += c.subtreeW + H_GAP;
    }
  };
  place(root, 0, 0);

  // Hide the synthetic root visually by pulling its children up one row.
  if (root.children.length > 0) {
    const lift = (n: ChartNode): void => {
      n.y -= (CARD_H + V_GAP);
      for (const c of n.children) lift(c);
    };
    for (const c of root.children) lift(c);
  }
  const width = root.subtreeW + 80;
  const height = (CARD_H + V_GAP) * maxDepth + CARD_H + 80;
  return { width, height };
}

function collectAll(root: ChartNode): ChartNode[] {
  const out: ChartNode[] = [];
  const walk = (n: ChartNode): void => {
    if (n.member) out.push(n);
    for (const c of n.children) walk(c);
  };
  walk(root);
  return out;
}

function findNodeById(root: ChartNode, id: string): ChartNode | null {
  if (root.id === id) return root;
  for (const c of root.children) {
    const f = findNodeById(c, id);
    if (f) return f;
  }
  return null;
}

// ---------- component ----------

export function OrgChartModal({ members, onClose }: { members: TeamMemberRecord[]; onClose: () => void }) {
  const [zoom, setZoom] = useState(1);
  const [search, setSearch] = useState("");
  const [hideInactive, setHideInactive] = useState(true);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [hireFilter, setHireFilter] = useState<string>("all");
  const [exporting, setExporting] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  // Lock body scroll for the duration of the overlay.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const visibleMembers = useMemo(() => {
    let xs = members;
    if (hideInactive) xs = xs.filter(m => m.is_active);
    if (hireFilter !== "all") xs = xs.filter(m => (m.hire_type ?? "direct") === hireFilter);
    return xs;
  }, [members, hideInactive, hireFilter]);

  const { tree, dims } = useMemo(() => {
    let scope = visibleMembers;
    if (focusId) {
      // Focus: keep focused member, descendants, and ancestor chain.
      const keep = new Set<string>([focusId]);
      let frontier = [focusId];
      while (frontier.length) {
        const next: string[] = [];
        for (const f of frontier) {
          for (const m of scope) {
            if (m.reports_to_id === f && !keep.has(m.id)) {
              keep.add(m.id);
              next.push(m.id);
            }
          }
        }
        frontier = next;
      }
      const byId = new Map(scope.map(m => [m.id, m] as const));
      const visited = new Set<string>();
      let cursor: string | null | undefined = byId.get(focusId)?.reports_to_id ?? null;
      let hops = 0;
      while (cursor && !visited.has(cursor) && hops < 64) {
        keep.add(cursor);
        visited.add(cursor);
        cursor = byId.get(cursor)?.reports_to_id ?? null;
        hops++;
      }
      scope = scope.filter(m => keep.has(m.id));
    }
    const t = buildTree(scope);
    const d = applyLayout(t);
    return { tree: t, dims: d };
  }, [visibleMembers, focusId]);

  const allNodes = useMemo(() => collectAll(tree), [tree]);

  const matched = useMemo(() => {
    if (!search.trim()) return new Set<string>();
    const q = search.trim().toLowerCase();
    return new Set(allNodes.filter(n => {
      const m = n.member!;
      return m.name.toLowerCase().includes(q)
        || (m.team_function ?? "").toLowerCase().includes(q)
        || (m.department_name ?? "").toLowerCase().includes(q)
        || (m.section_name ?? "").toLowerCase().includes(q);
    }).map(n => n.id));
  }, [allNodes, search]);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
      const pageW = 420;
      const pageH = 297;
      const margin = 12;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2 - 14; // reserve top strip for title
      // Title strip
      pdf.setFontSize(14);
      pdf.setTextColor(20, 20, 20);
      const focusedName = focusId
        ? (allNodes.find(n => n.id === focusId)?.member?.name ?? null)
        : null;
      const titleSuffix = focusedName ? ` — ${focusedName}` : " — Full Org";
      pdf.text(`Organization Chart${titleSuffix}`, margin, margin + 6);
      pdf.setFontSize(9);
      pdf.setTextColor(110, 110, 110);
      const today = new Date().toISOString().slice(0, 10);
      pdf.text(`Generated ${today} · ${allNodes.length} people`, margin, margin + 11);
      pdf.setDrawColor(180, 180, 190);
      pdf.setLineWidth(0.4);
      pdf.line(margin, margin + 13, pageW - margin, margin + 13);

      // Per-department legend (deterministic colour by name hash).
      const deptColors = new Map<string, [number, number, number]>();
      const hashColor = (s: string): [number, number, number] => {
        let h = 0;
        for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
        const palette: [number, number, number][] = [
          [70, 130, 180], [100, 150, 90], [200, 130, 60], [160, 100, 170],
          [90, 160, 170], [200, 100, 110], [130, 130, 80], [80, 110, 200],
          [170, 80, 130], [80, 170, 130], [200, 170, 60], [120, 100, 70],
        ];
        return palette[h % palette.length];
      };
      for (const n of allNodes) {
        const d = n.member?.department_name;
        if (d && !deptColors.has(d)) deptColors.set(d, hashColor(d));
      }
      const legendW = 38;
      const legendX = pageW - margin - legendW;
      const legendY = margin + 18;
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      pdf.text("Departments", legendX, legendY);
      pdf.setFontSize(7.5);
      let ly = legendY + 4;
      const sortedDepts = Array.from(deptColors.entries()).sort((a, b) => a[0].localeCompare(b[0]));
      for (const [dept, rgb] of sortedDepts) {
        pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
        pdf.rect(legendX, ly - 2.5, 3, 3, "F");
        pdf.setTextColor(40, 40, 40);
        const label = pdf.splitTextToSize(dept, legendW - 5)[0];
        pdf.text(label, legendX + 4.5, ly);
        ly += 4;
        if (ly > pageH - margin - 4) break;
      }

      const w = dims.width;
      const h = dims.height;
      const chartAvailW = availW - legendW - 4;
      const S = Math.min(chartAvailW / w, availH / h);
      const offsetX = margin + (chartAvailW - w * S) / 2;
      const offsetY = margin + 14 + (availH - h * S) / 2;
      const px = (n: number) => n * S;

      // Connectors first, then cards on top (so lines tuck under the cards).
      pdf.setDrawColor(150, 150, 160);
      pdf.setLineWidth(0.25);
      const drawConnectors = (n: ChartNode): void => {
        for (const c of n.children) {
          if (n.member) {
            const x1 = offsetX + px(n.x + CARD_W / 2);
            const y1 = offsetY + px(n.y + CARD_H);
            const x2 = offsetX + px(c.x + CARD_W / 2);
            const y2 = offsetY + px(c.y);
            const midY = (y1 + y2) / 2;
            pdf.line(x1, y1, x1, midY);
            pdf.line(x1, midY, x2, midY);
            pdf.line(x2, midY, x2, y2);
          }
          drawConnectors(c);
        }
      };
      drawConnectors(tree);

      // Cards
      for (const n of allNodes) {
        const m = n.member!;
        const x = offsetX + px(n.x);
        const y = offsetY + px(n.y);
        const cw = px(CARD_W);
        const ch = px(CARD_H);
        pdf.setFillColor(255, 255, 255);
        const dn = m.department_name;
        if (dn && deptColors.has(dn)) {
          const [r, g, bl] = deptColors.get(dn)!;
          pdf.setDrawColor(r, g, bl);
        } else {
          pdf.setDrawColor(60, 80, 120);
        }
        pdf.setLineWidth(0.45);
        pdf.roundedRect(x, y, cw, ch, 1.5, 1.5, "FD");
        if (dn && deptColors.has(dn)) {
          const [r, g, bl] = deptColors.get(dn)!;
          pdf.setFillColor(r, g, bl);
          pdf.rect(x, y, cw, 0.8, "F");
        }
        pdf.setFontSize(9);
        pdf.setTextColor(20, 20, 20);
        const padL = x + 2.5;
        let ty = y + 5;
        // Name
        pdf.setFont("helvetica", "bold");
        const name = pdf.splitTextToSize(m.name, cw - 5)[0];
        pdf.text(name, padL, ty);
        ty += 4;
        // Function
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(70, 70, 80);
        if (m.team_function) {
          const fn = pdf.splitTextToSize(m.team_function, cw - 5)[0];
          pdf.text(fn, padL, ty);
          ty += 3.4;
        }
        // Dept · section
        const deptLine = [m.department_name, m.section_name].filter(Boolean).join(" · ");
        if (deptLine) {
          const dl = pdf.splitTextToSize(deptLine, cw - 5)[0];
          pdf.setTextColor(110, 110, 120);
          pdf.text(dl, padL, ty);
          ty += 3.4;
        }
        // Hire-type pill
        const meta = (m.hire_type ?? "direct").toLowerCase();
        const pillLabel = meta === "contract" ? "Contract" : meta === "consultant" ? "Consultant" : "Direct";
        const pillW = pdf.getTextWidth(pillLabel) + 3;
        pdf.setFillColor(240, 245, 255);
        pdf.setDrawColor(180, 195, 220);
        pdf.roundedRect(padL, ty - 2, pillW, 4, 0.8, 0.8, "FD");
        pdf.setTextColor(50, 70, 110);
        pdf.text(pillLabel, padL + 1.5, ty + 1);
        // Identity / dept-head badges (right side)
        let rx = x + cw - 2.5;
        if (m.is_department_head && m.department_id) {
          const lbl = "Head";
          const w0 = pdf.getTextWidth(lbl) + 3;
          pdf.setFillColor(220, 240, 255);
          pdf.setDrawColor(120, 180, 220);
          pdf.roundedRect(rx - w0, ty - 2, w0, 4, 0.8, 0.8, "FD");
          pdf.setTextColor(20, 80, 140);
          pdf.text(lbl, rx - w0 + 1.5, ty + 1);
          rx -= w0 + 1.5;
        }
        const role = identityRoleFor(m.name);
        if (role) {
          const lbl = `🔑 ${role}`;
          const w0 = pdf.getTextWidth(lbl) + 3;
          pdf.setFillColor(255, 245, 215);
          pdf.setDrawColor(220, 180, 80);
          pdf.roundedRect(rx - w0, ty - 2, w0, 4, 0.8, 0.8, "FD");
          pdf.setTextColor(140, 100, 20);
          pdf.text(lbl, rx - w0 + 1.5, ty + 1);
        }
      }
      pdf.save(`org-chart-${today}.pdf`);
    } finally {
      setExporting(false);
    }
  }, [tree, dims, allNodes, focusId]);

  const [pulsedId, setPulsedId] = useState<string | null>(null);
  useEffect(() => {
    if (matched.size === 0) {
      setPulsedId(null);
      return;
    }
    const firstId = allNodes.find(n => matched.has(n.id))?.id;
    if (!firstId) return;
    setPulsedId(firstId);
    const el = stageRef.current?.querySelector<HTMLElement>(`[data-org-chart-card="${firstId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    const t = setTimeout(() => setPulsedId(null), 1600);
    return () => clearTimeout(t);
  }, [matched, allNodes]);

  // ---------- render ----------

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#0a0a23]/98 backdrop-blur"
      data-testid="modal-org-chart"
    >
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-white/10 bg-[#10103a]">
        <div className="flex items-center gap-2 mr-auto">
          <Maximize2 className="w-4 h-4 text-cyan-300" />
          <h2 className="text-base font-semibold text-white">Org Chart</h2>
          <span className="text-xs text-white/45">({allNodes.length} of {members.length} people shown)</span>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-white/40 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, function, dept…"
            className="pl-7 h-8 bg-white/5 border-white/10 text-white text-sm w-56"
            data-testid="input-org-chart-search"
          />
        </div>
        <select
          value={hireFilter}
          onChange={(e) => setHireFilter(e.target.value)}
          className="h-8 rounded bg-slate-900 border border-white/10 text-white text-xs px-2"
          data-testid="select-org-chart-hire-filter"
        >
          <option value="all" className="bg-slate-900 text-white">All hire types</option>
          <option value="direct" className="bg-slate-900 text-white">Direct</option>
          <option value="contract" className="bg-slate-900 text-white">Contract</option>
          <option value="consultant" className="bg-slate-900 text-white">Consultant</option>
        </select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHideInactive(!hideInactive)}
          className="text-white/70 hover:text-white"
          data-testid="button-org-chart-hide-inactive"
          title={hideInactive ? "Show inactive members" : "Hide inactive members"}
        >
          {hideInactive ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
          {hideInactive ? "Inactive hidden" : "Showing inactive"}
        </Button>
        {focusId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFocusId(null)}
            className="text-amber-300 hover:text-amber-200"
            data-testid="button-org-chart-clear-focus"
          >
            <Crosshair className="w-4 h-4 mr-1" /> Clear focus
          </Button>
        )}
        <div className="flex items-center gap-1 border border-white/10 rounded">
          <Button
            variant="ghost" size="sm"
            onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}
            data-testid="button-org-chart-zoom-out"
            className="h-8 px-2 text-white/70"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="text-xs text-white/60 w-12 text-center hover:text-white"
            data-testid="button-org-chart-zoom-reset"
            title="Reset zoom to 100%"
          >
            {Math.round(zoom * 100)}%
          </button>
          <Button
            variant="ghost" size="sm"
            onClick={() => setZoom(z => Math.min(2.5, z + 0.1))}
            data-testid="button-org-chart-zoom-in"
            className="h-8 px-2 text-white/70"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        <Button
          onClick={exportPdf}
          disabled={exporting || allNodes.length === 0}
          className="bg-cyan-500/20 text-cyan-200 border border-cyan-400/40 hover:bg-cyan-500/30"
          data-testid="button-org-chart-export-pdf"
        >
          <Download className="w-4 h-4 mr-1" />
          {exporting ? "Exporting…" : "Export PDF (A3 Landscape)"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-white/70 hover:text-white"
          data-testid="button-org-chart-close"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Stage */}
      <div ref={stageRef} className="flex-1 overflow-auto bg-[#0c0c2a]" data-testid="stage-org-chart">
        {allNodes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-white/40 text-sm">
            No team members match the current filters.
          </div>
        ) : (
          <div
            className="relative"
            style={{
              width: dims.width,
              height: dims.height,
              padding: 40,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
          >
            {/* Connectors */}
            <svg
              className="absolute top-0 left-0 pointer-events-none"
              width={dims.width}
              height={dims.height}
              style={{ left: 40, top: 40 }}
            >
              {(() => {
                const lines: JSX.Element[] = [];
                const walk = (n: ChartNode): void => {
                  for (const c of n.children) {
                    if (n.member) {
                      const x1 = n.x + CARD_W / 2;
                      const y1 = n.y + CARD_H;
                      const x2 = c.x + CARD_W / 2;
                      const y2 = c.y;
                      const midY = (y1 + y2) / 2;
                      const path = `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
                      lines.push(
                        <path
                          key={`${n.id}-${c.id}`}
                          d={path}
                          fill="none"
                          stroke="rgba(120,160,220,0.45)"
                          strokeWidth={1.2}
                        />
                      );
                    }
                    walk(c);
                  }
                };
                walk(tree);
                return lines;
              })()}
            </svg>

            {/* Cards */}
            {allNodes.map((n) => {
              const m = n.member!;
              const role = identityRoleFor(m.name);
              const meta = (m.hire_type ?? "direct").toLowerCase();
              const pillLabel = meta === "contract" ? "Contract" : meta === "consultant" ? "Consultant" : "Direct";
              const pillClass = meta === "contract"
                ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                : meta === "consultant"
                ? "bg-violet-500/15 text-violet-300 border-violet-500/30"
                : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
              const isMatch = matched.has(m.id);
              const dim = matched.size > 0 && !isMatch;
              const pulsing = pulsedId === m.id;
              return (
                <div
                  key={m.id}
                  data-org-chart-card={m.id}
                  className={`absolute rounded-lg border bg-[#13133b] p-2.5 shadow transition-opacity ${dim ? "opacity-25" : "opacity-100"} ${isMatch ? "border-cyan-400 ring-2 ring-cyan-400/40" : "border-white/15"} ${pulsing ? "animate-pulse ring-4 ring-cyan-300/70" : ""}`}
                  style={{
                    left: 40 + n.x,
                    top: 40 + n.y,
                    width: CARD_W,
                    height: CARD_H,
                  }}
                  data-testid={`card-org-chart-${m.id}`}
                  onClick={() => setFocusId(m.id)}
                  title="Click to focus this subtree (keeps the chain of command above)"
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                      {m.team_function && (
                        <div className="text-xs text-white/55 truncate">{m.team_function}</div>
                      )}
                      {(m.department_name || m.section_name) && (
                        <div className="text-xs text-white/40 truncate">
                          {[m.department_name, m.section_name].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {role && (
                      <span
                        className="shrink-0 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-2xs bg-amber-500/15 text-amber-300 border border-amber-500/30"
                        title={`Identity-bound: ${role}`}
                      >
                        <Key className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-2 left-2.5 right-2.5 flex items-center gap-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-2xs border ${pillClass}`}>{pillLabel}</span>
                    {m.is_department_head && m.department_id && (
                      <span className="px-1.5 py-0.5 rounded text-2xs bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                        Head
                      </span>
                    )}
                    {!m.is_active && (
                      <span className="px-1.5 py-0.5 rounded text-2xs bg-white/5 text-white/40 border border-white/10">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-white/10 bg-[#10103a] text-xs text-white/45 flex items-center gap-4 flex-wrap">
        <span>Click any card to focus its subtree.</span>
        <span>Search highlights matches; non-matches dim.</span>
        <span className="ml-auto">Display-only — does not affect permissions, sign-off, or KPI rollup.</span>
      </div>
    </div>
  );
}
